import {
  createContext, useCallback, useContext, useState, type ComponentType, type ReactNode,
} from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import {
  Dialog as UIDialog, DialogContent as UIDialogContent, DialogDescription as UIDialogDescription,
  DialogHeader as UIDialogHeader, DialogTitle as UIDialogTitle,
} from "@/components/ui/dialog";

// The ui components are untyped .jsx; give them permissive prop types here.
const Dialog = UIDialog as ComponentType<any>;
const DialogContent = UIDialogContent as ComponentType<any>;
const DialogDescription = UIDialogDescription as ComponentType<any>;
const DialogHeader = UIDialogHeader as ComponentType<any>;
const DialogTitle = UIDialogTitle as ComponentType<any>;

const FIELD = "h-11 w-full rounded-2xl border border-input bg-card px-4 text-base outline-none " +
  "focus-visible:ring-2 focus-visible:ring-ring";
export const BTN = "inline-flex h-11 items-center justify-center rounded-full px-5 text-sm font-bold " +
  "disabled:opacity-50";
export const BTN_PRIMARY = `${BTN} bg-primary text-primary-foreground`;
export const BTN_QUIET = `${BTN} border border-border bg-card`;
export const BTN_DANGER = `${BTN} bg-destructive text-destructive-foreground`;

export function Modal({ open, onClose, title, note, children, tall = false, top = false }: {
  open: boolean; onClose: () => void; title: string; note?: ReactNode; children: ReactNode;
  tall?: boolean;     // a full-screen page on a phone, with its own Back bar that stays in view
  top?: boolean;      // always above any other open box (the password check)
}) {
  // Dialogs opened later sit above earlier ones. The password box is opened last, on top of
  // whatever asked for it; `top` makes that true even if the order ever changes.
  const layer = top ? "z-[60]" : "";
  return (
    <Dialog open={open} onOpenChange={(o: boolean) => { if (!o) onClose(); }}>
      <DialogContent className={tall
        ? `h-[100dvh] max-h-[100dvh] w-full max-w-2xl content-start gap-4 overflow-y-auto rounded-none p-5 pt-0 sm:h-[90dvh] sm:rounded-3xl [&>button.absolute]:hidden ${layer}`
        : `max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-md gap-5 overflow-y-auto rounded-3xl p-6 sm:rounded-3xl ${layer}`}>
        {tall && (
          <div className="sticky top-0 z-10 -mx-5 flex items-center gap-2 border-b border-border bg-background px-3 py-2">
            <button type="button" onClick={onClose} data-testid="sheet-back"
              className="inline-flex h-11 items-center gap-1 rounded-full px-3 text-sm font-bold text-primary">
              <span aria-hidden>←</span> Back
            </button>
          </div>
        )}
        <DialogHeader className="space-y-1.5 text-left">
          <DialogTitle className="pr-8 text-lg font-extrabold tracking-tight">{title}</DialogTitle>
          {note
            ? <DialogDescription className="text-sm text-muted-foreground">{note}</DialogDescription>
            : <DialogDescription className="sr-only">{title}</DialogDescription>}
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

/** Show an error, unless it is only the admin closing the password box. */
export function fail(e: any) {
  if (e?.cancelled) return;
  toast.error(apiError(e));
}

// ---- The password check before anything that deletes, pauses or moves an account ----

type Guard = <T>(fn: () => Promise<T>) => Promise<T>;
const GuardContext = createContext<Guard>((fn) => fn());
export const useGuard = () => useContext(GuardContext);

export function UnlockProvider({ children }: { children: ReactNode }) {
  const [waiting, setWaiting] = useState<null | { ok: () => void; no: (e: any) => void }>(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [wrong, setWrong] = useState<string | null>(null);

  const guard = useCallback(async function <T>(fn: () => Promise<T>): Promise<T> {
    try {
      return await fn();
    } catch (e: any) {
      const locked = e?.response?.status === 403
        && e?.response?.data?.detail?.code === "unlock_required";
      if (!locked) throw e;
      await new Promise<void>((ok, no) => setWaiting({ ok, no }));
      return await fn();
    }
  }, []) as Guard;
  // If two things ever asked at once, the second would replace the first. They cannot today:
  // every caller is a box that blocks the page behind it.

  const close = () => {
    waiting?.no(Object.assign(new Error("cancelled"), { cancelled: true }));
    setWaiting(null); setPassword(""); setWrong(null);
  };
  const submit = async () => {
    setBusy(true); setWrong(null);
    try {
      await api.post("/admin/unlock", { password });
      waiting?.ok();
      setWaiting(null); setPassword("");
    } catch (e: any) {
      setPassword("");
      if (e?.response?.status === 429) {
        // Too many wrong tries: the server has signed this browser out. Start again clean.
        toast.error(apiError(e));
        close();
        window.location.assign("/login");
        return;
      }
      setWrong(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <GuardContext.Provider value={guard}>
      {children}
      <Modal top open={waiting !== null} onClose={close} title="Enter your password"
        note="This change needs your password. After that, you won't be asked again for 10 minutes.">
        <form onSubmit={(e) => { e.preventDefault(); if (password && !busy) submit(); }}>
          <input type="text" autoComplete="username" hidden readOnly value="" />
          <input type="password" autoComplete="current-password" autoFocus value={password}
            onChange={(e) => setPassword(e.target.value)} placeholder="Password"
            aria-label="Password" className={FIELD} data-testid="unlock-password" />
          {wrong && <p className="mt-2 text-sm font-semibold text-destructive dark:text-red-400" role="alert">{wrong}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" className={BTN_QUIET} onClick={close}>Cancel</button>
            <button type="submit" className={BTN_PRIMARY} disabled={!password || busy}
              data-testid="unlock-submit">Continue</button>
          </div>
        </form>
      </Modal>
    </GuardContext.Provider>
  );
}

// ---- One confirmation box for every change ----

export function Confirm({ open, onClose, title, body, action, danger = false, typed, ok = true, children, run }: {
  open: boolean; onClose: () => void; title: string; body: ReactNode; action: string;
  danger?: boolean;
  typed?: string;           // the exact words the admin has to type first (case does not matter)
  ok?: boolean;             // false while something the box needs is still missing
  children?: ReactNode;     // extra choices shown above the buttons
  run: () => Promise<void>;
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const norm = (s: string) => s.trim().replace(/\s+/g, " ").toLowerCase();
  const ready = ok && (!typed || norm(text) === norm(typed));
  const done = () => { setText(""); onClose(); };
  const go = async () => {
    setBusy(true);
    try {
      await run();
      done();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={open} onClose={done} title={title} note={body}>
      <form className="grid gap-5" onSubmit={(e) => { e.preventDefault(); if (ready && !busy) go(); }}>
      {children}
      {typed && (
        <div>
          <label className="text-sm font-semibold" htmlFor="confirm-typed">
            Type <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[13px]">{typed}</span> to confirm
          </label>
          <input id="confirm-typed" value={text} onChange={(e) => setText(e.target.value)}
            autoCapitalize="off" autoCorrect="off" spellCheck={false}
            className={`${FIELD} mt-2`} data-testid="confirm-typed" />
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" className={BTN_QUIET} onClick={done}>Cancel</button>
        <button type="submit" className={danger ? BTN_DANGER : BTN_PRIMARY}
          disabled={!ready || busy} data-testid="confirm-go">{action}</button>
      </div>
      </form>
    </Modal>
  );
}

export { FIELD };
